/** The custom-row draft lane (SA-381, step-model E3 spec D15/§4): an author
 * edits a custom row's WHOLE document. The server stores the draft as typed;
 * `/draft/validate` is the checker and `/draft/publish` mints the version. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import { writeConflict, type WriteConflict } from '@/utils/writeConflict'
import type {
  WorkflowDraftPublishResponse,
  WorkflowDraftRead,
  WorkflowDraftValidateResponse,
} from '@/types/workflowDefinition'

export function workflowDraftQueryKey(workflowId: string) {
  return ['workflows', 'draft', workflowId] as const
}

export function useWorkflowDraft(
  workflowId: string | undefined,
): UseQueryResult<WorkflowDraftRead, Error> {
  return useQuery<WorkflowDraftRead, Error>({
    queryKey: workflowDraftQueryKey(workflowId ?? ''),
    queryFn: () => apiClient.get<WorkflowDraftRead>(`/api/workflows/${workflowId}/draft`),
    enabled: !!workflowId,
    // Another author may have saved since: the stamp must be fresh when an
    // edit session begins, or the first save is a guaranteed 409.
    staleTime: 0,
  })
}

/** What a draft-lane write is based on (SA-404): the stored draft's stamp
 * (null = none stored) and the live version's number (null = the row has
 * never published, sent as 0). The server refuses a write whose basis moved. */
export interface DraftBasis {
  expectedUpdatedAt: string | null
  baseVersionNumber: number | null
}

/** The draft lane's names for the shared reader (`utils/writeConflict`). */
export type DraftConflict = WriteConflict
export const draftConflict = writeConflict

/** A save whose basis moved rejects with a 409 — see `draftConflict`. A first
 * save says `expected_absent`: the wire's "I expect NO stored draft". */
export function useSaveWorkflowDraft(
  workflowId: string,
): UseMutationResult<WorkflowDraftRead, Error, { draft: Record<string, unknown> } & DraftBasis> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ draft, expectedUpdatedAt, baseVersionNumber }) =>
      apiClient.put<WorkflowDraftRead>(`/api/workflows/${workflowId}/draft`, {
        draft,
        ...(expectedUpdatedAt === null ? { expected_absent: true } : { expected_updated_at: expectedUpdatedAt }),
        expected_version_number: baseVersionNumber ?? 0,
      }),
    onSuccess: (data, { expectedUpdatedAt }) => {
      // The row's FIRST stored draft flips `has_draft` on the detail and the
      // gallery chip; later saves change nothing those views show.
      if (expectedUpdatedAt === null) queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.setQueryData(workflowDraftQueryKey(workflowId), data)
    },
  })
}

/** Discards the draft this session knows — a newer one is a 409 and stays. */
export function useDiscardWorkflowDraft(
  workflowId: string,
): UseMutationResult<void, Error, { expectedUpdatedAt: string | null }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ expectedUpdatedAt }) =>
      apiClient.delete(
        expectedUpdatedAt === null
          ? `/api/workflows/${workflowId}/draft`
          : // Encoded: a bare `+` in a query string is a space.
            `/api/workflows/${workflowId}/draft?expected_updated_at=${encodeURIComponent(expectedUpdatedAt)}`,
      ),
    onSuccess: () => {
      // The detail's `has_draft` and the gallery chip change with it.
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
  })
}

/** Stateless: validates the document in the body, not the stored draft. */
export function useValidateWorkflowDraft(
  workflowId: string,
): UseMutationResult<WorkflowDraftValidateResponse, Error, Record<string, unknown>> {
  return useMutation({
    mutationFn: (definition: Record<string, unknown>) =>
      apiClient.post<WorkflowDraftValidateResponse>(`/api/workflows/${workflowId}/draft/validate`, {
        definition,
      }),
  })
}

/** Publishes the STORED draft — save first. The basis makes it THIS
 * session's stored draft, against the version its diff was shown for. */
export function usePublishWorkflowDraft(
  workflowId: string,
): UseMutationResult<WorkflowDraftPublishResponse, Error, DraftBasis> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ expectedUpdatedAt, baseVersionNumber }) =>
      apiClient.post<WorkflowDraftPublishResponse>(`/api/workflows/${workflowId}/draft/publish`, {
        ...(expectedUpdatedAt === null ? {} : { expected_updated_at: expectedUpdatedAt }),
        expected_version_number: baseVersionNumber ?? 0,
      }),
    onSuccess: () => {
      // Library, detail, definition, versions and the draft all change shape.
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
  })
}
